// app/hooks/use-user-review.tsx
// Custom hook to check if a user has reviewed a product and get review status
// Used to conditionally show/hide "Write a Review" buttons and display review status
//
// Related files:
// - ~/utils/cache-manager.ts - Unified caching system
// - ~/context/review-context.tsx - Review data fetching
// - ~/components/product-review-modal.tsx - Review submission
// - ~/components/product-reviews.tsx - Review display

import { useState, useEffect, useCallback, useRef } from 'react';
import { useReviewCache, type Review } from '~/context/review-context';
import { createCache, CACHE_PRESETS } from '~/utils/cache-manager';

// Utility function to get a cookie by name
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = '; ' + document.cookie;
  const parts = value.split('; ' + name + '=');
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

interface UserReviewStatus {
  hasReviewed: boolean;
  review: Review | null;
  status: 'published' | 'pending' | 'rejected' | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

// ⚡ PERFORMANCE: Unified cache for user reviews
// Uses shared CacheManager for consistent caching behavior across the app
// TTL: 30 minutes (reviews don't change frequently after submission)
// Max size: 50 entries (prevents memory leaks in long sessions)
const reviewCache = createCache<Review | null>(CACHE_PRESETS.LONG, 'UserReviewCache');

export function useUserReview(productId: string | undefined): UserReviewStatus {
  const { getUserReview } = useReviewCache();
  const [review, setReview] = useState<Review | null>(null);
  // Start with loading state to prevent showing button before checking for existing review
  // Will be set to false after cache check or fetch completes
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const lastFetchTimeRef = useRef<number>(0); // Track when we last fetched to enable stale-while-revalidate

  // Get current userId to include in cache key
  const getCurrentUserId = useCallback(() => {
    if (typeof window === 'undefined') return null;
    return getCookie('catalystUserId');
  }, []);

  // Generate cache key that includes userId to prevent cross-user cache pollution
  // Only logged-in users can review, so userId is always required
  const getCacheKey = useCallback((pid: string, uid: string | null) => {
    // Include userId in cache key to prevent showing wrong user's review status
    if (!uid) {
      // No userId means user is logged out - return null to indicate no cache possible
      return null;
    }
    return `${pid}:${uid}`;
  }, []);

  const fetchUserReview = useCallback(async () => {
    // Only run on client side (SSR safety)
    if (typeof window === 'undefined') {
      return;
    }

    if (!productId) {
      setReview(null);
      setIsLoading(false);
      return;
    }

    // Get current userId for cache key
    const userId = getCurrentUserId();

    // Check if user is logged in (required for reviews)
    if (!userId) {
      // Not logged in - no review possible
      setReview(null);
      setIsLoading(false);
      return;
    }

    const cacheKey = getCacheKey(productId, userId);
    if (!cacheKey) {
      // Should never happen if userId exists, but safety check
      setReview(null);
      setIsLoading(false);
      return;
    }

    // ⚡ PERFORMANCE: Check cache first
    const cached = reviewCache.get(cacheKey);
    if (cached !== null) {
      // Cache hit - use immediately, no API call needed
      setReview(cached);
      setIsLoading(false);
      return;
    }

    // ⚡ RACE CONDITION PREVENTION: Check if already fetching
    if (reviewCache.isFetching(cacheKey)) {
      return; // Another fetch is already in progress, skip duplicate
    }

    // Mark as fetching to prevent concurrent requests
    reviewCache.markFetching(cacheKey);

    setIsLoading(true);
    try {
      const userReview = await getUserReview(productId, userId);
      setReview(userReview);

      // Cache the result (automatically clears fetching flag)
      reviewCache.set(cacheKey, userReview);

      // Update last fetch time for stale-while-revalidate
      lastFetchTimeRef.current = Date.now();
    } catch (error) {
      console.error('Error fetching user review:', error);
      setReview(null);

      // Cache null result to prevent refetch spam (automatically clears fetching flag)
      reviewCache.set(cacheKey, null);
    } finally {
      setIsLoading(false);
    }
  }, [productId, getUserReview, getCurrentUserId, getCacheKey]);

  // ⚡ PERFORMANCE: Load from cache immediately if available (synchronous, non-blocking)
  // Uses stale-while-revalidate pattern: show cached data immediately, refresh in background
  // This ensures modal opens instantly, then updates if review status changed
  useEffect(() => {
    if (!productId) {
      setReview(null);
      setHasFetched(false);
      setIsLoading(false);
      return;
    }

    const userId = getCurrentUserId();
    const cacheKey = getCacheKey(productId, userId);

    // If no userId (logged out), no cache possible - show no review
    if (!cacheKey) {
      setReview(null);
      setHasFetched(true);
      setIsLoading(false);
      return;
    }

    // Try to get cached review (returns null if not found or expired)
    const cached = reviewCache.get(cacheKey);

    if (cached !== null) {
      // Cache hit - use immediately for instant UI
      setReview(cached);
      setHasFetched(true);
      setIsLoading(false);

      // 🔄 STALE-WHILE-REVALIDATE: If cache is older than 5 minutes, refresh in background
      // This ensures users see updated review status (pending -> approved) without waiting 30 min
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      const timeSinceLastFetch = now - lastFetchTimeRef.current;

      // If we haven't fetched recently (or never fetched), refresh in background
      // This ensures review status updates (pending -> approved) are seen quickly
      if (timeSinceLastFetch > fiveMinutes || lastFetchTimeRef.current === 0) {
        // Defer refresh to avoid blocking render - show cached data immediately
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            fetchUserReview().catch((error) => {
              console.error('Background refresh error:', error);
            });
          });
        });
      }
    } else if (!hasFetched) {
      // Cache miss AND haven't fetched yet - need to fetch
      // CRITICAL: Don't show button until we verify user hasn't already reviewed
      setReview(null);
      setHasFetched(false);
      setIsLoading(true); // Show loading state while checking for existing review
    }
    // Cache miss but already fetched - fetch must have completed with null result
    // Don't change loading state - let the fetch effect handle it
  }, [productId, getCurrentUserId, getCacheKey, hasFetched, fetchUserReview]);

  // Clear cache when user logs out/in to prevent stale data
  useEffect(() => {
    const handleUserChange = () => {
      // When user logs out or logs in, invalidate cached reviews for this product
      // This ensures fresh data is fetched for the new user session
      if (productId) {
        const userId = getCurrentUserId();
        const cacheKey = getCacheKey(productId, userId);
        if (cacheKey) {
          reviewCache.invalidate(cacheKey);
        }
        setHasFetched(false);
        setReview(null);
      }
    };

    // Listen for login/logout events
    if (typeof window !== 'undefined') {
      window.addEventListener('user:login', handleUserChange);
      window.addEventListener('user:logout', handleUserChange);
      // Also listen for storage changes (cookie changes)
      window.addEventListener('storage', handleUserChange);

      return () => {
        window.removeEventListener('user:login', handleUserChange);
        window.removeEventListener('user:logout', handleUserChange);
        window.removeEventListener('storage', handleUserChange);
      };
    }
  }, [productId, getCurrentUserId, getCacheKey]);

  // ⚡ PERFORMANCE: Trigger fetch when needed (after cache check)
  // This ensures we fetch fresh data if cache miss
  useEffect(() => {
    if (!hasFetched && productId) {
      const userId = getCurrentUserId();
      const cacheKey = getCacheKey(productId, userId);

      // If no userId, don't fetch (user not logged in)
      if (!cacheKey) {
        setHasFetched(true);
        setIsLoading(false); // Not logged in, so stop loading
        return;
      }

      // Check if we need to fetch (not in cache or expired)
      const cached = reviewCache.get(cacheKey);
      const needsFetch = cached === null;

      if (needsFetch) {
        // Fetch immediately without delay to show results faster
        fetchUserReview().catch((error) => {
          console.error('Error fetching user review:', error);
          // Ensure hasFetched is set even on error to prevent infinite retries
          setHasFetched(true);
        });
        setHasFetched(true);
      } else {
        // Cache hit - mark as fetched to prevent unnecessary refetch
        setHasFetched(true);
      }
    }
  }, [productId, hasFetched, fetchUserReview, getCurrentUserId, getCacheKey]);

  // ⚡ AUTO-REFRESH: Poll for status updates every 30 seconds if review is pending
  // This ensures users see when their review gets approved/rejected
  useEffect(() => {
    if (!productId || !review || review.status?.toLowerCase() !== 'pending') {
      return;
    }

    // Poll every 30 seconds to check for status updates
    const pollInterval = setInterval(() => {
      fetchUserReview();
    }, 30000); // 30 seconds

    return () => clearInterval(pollInterval);
  }, [productId, review, fetchUserReview]);

  const hasReviewed = review !== null;
  const reviewStatus = review?.status?.toLowerCase();
  const status =
    reviewStatus === 'published'
      ? 'published'
      : reviewStatus === 'pending' || reviewStatus === 'pending_approval'
        ? 'pending'
        : reviewStatus === 'rejected' || reviewStatus === 'declined'
          ? 'rejected'
          : null;

  // Refetch function that clears cache and fetches fresh data
  // Used after review submission to update status immediately
  const refetch = useCallback(async () => {
    if (!productId) return;

    const userId = getCurrentUserId();
    const cacheKey = getCacheKey(productId, userId);

    if (!cacheKey) {
      // User not logged in, nothing to refetch
      return;
    }

    // Invalidate cache to force fresh fetch
    reviewCache.invalidate(cacheKey);
    setHasFetched(false);

    // Fetch fresh data
    await fetchUserReview();
  }, [productId, fetchUserReview, getCurrentUserId, getCacheKey]);

  return {
    hasReviewed,
    review,
    status,
    isLoading,
    refetch,
  };
}
